import { esc } from '../ui/modal';
import { VMOS_DEPLOY_SCRIPT, vmosDeployCommand } from './farm-docker-config';
import { t } from '../ui/lang';
import { downloadMd } from './farm-setup-shared';
/** Build the pre-filled VMOS deploy command for a farm (script is a static mirror). */
export function buildVmosSetup(name, key) {
    const relayHost = window.location.origin;
    const deployCmd = vmosDeployCommand({ relayHost, key });
    const mdContent = `# Farm setup (VMOS device) — ${name}

## 1. Download the deploy script

Use **Download deploy.vmos.sh** on the panel's VMOS tab, then \`chmod +x deploy.vmos.sh\`.

## 2. Run it

Run it from a workstation that has \`adb\`, with the box reachable over ADB at
\`<BOX_IP>:5555\`. Replace \`<BOX_IP>\` with your VMOS box IP.

\`\`\`bash
${deployCmd}
\`\`\`

The deploy seeds the box's marsbox supervisor (\`<BOX_IP>:5555\`) into the farm's device
store; the farm agent then auto-connects every running VMOS instance, new ones included.

## 3. Parameters

- \`<BOX_IP>\` — required, positional. The VMOS box IP (e.g. \`192.168.31.126\`).
- \`--relay-key <KEY>\` — required. The farm's relay key (already filled in above).
- \`--relay-host <URL>\` — panel relay URL (already filled in above).
- \`--image <ref>\` — optional. Image to pull/run (default \`alkhrameev/mobile-farm:latest\`).
- \`--memory <lim>\` — optional. Container memory limit (default \`4g\`).
- \`--cpus <n>\` — optional. Container CPU limit (default \`4\`).
- \`-h | --help\` — show help.

> After deploy, the panel's **Restart with update** is a no-op for VMOS; re-run this script
> (or with \`--no-pull\`) to recreate the container.
`;
    return { deployCmd, mdContent };
}
/**
 * Inner HTML for the VMOS deploy instructions (no modal chrome). Shared by the
 * farm-config modal and the farm-settings "Launch / Setup" tab (VMOS sub-tab).
 * The download/copy buttons carry data attributes wired by {@link wireVmosSetup}.
 */
export function vmosSetupHtml(s) {
    return `
        <p class="modal__text">${t('farms.vmos_step1')}</p>
        <div class="modal__actions" style="margin:6px 0 0">
            <button type="button" class="tbl-btn" data-vmos-download style="margin-right:auto">${t('farms.vmos_download')}</button>
        </div>
        <p class="modal__text" style="margin-top:14px">${t('farms.vmos_step2')}</p>
        <div class="launch-block">
            <pre class="launch-pre">${esc(s.deployCmd)}</pre>
            <button class="tbl-btn copy-key-btn" data-vmos-copy title="${t('common.copy')}">⎘ ${t('common.copy')}</button>
        </div>
        <p class="modal__text" style="margin-top:14px">${t('farms.vmos_note')}</p>
        <p class="modal__text" style="margin-top:14px">${t('farms.vmos_step3')}</p>
        <ul class="modal__text" style="margin:6px 0 0 18px;line-height:1.7">
            <li>${t('farms.vmos_p_box_ip')}</li>
            <li>${t('farms.vmos_p_relay_key')}</li>
            <li>${t('farms.vmos_p_relay_host')}</li>
            <li>${t('farms.vmos_p_image')}</li>
            <li>${t('farms.vmos_p_memory')}</li>
            <li>${t('farms.vmos_p_cpus')}</li>
            <li>${t('farms.vmos_p_help')}</li>
        </ul>
        <p class="modal__text" style="margin-top:12px;font-size:0.85em;opacity:0.7">
            ${t('farms.vmos_after_deploy')}
        </p>
        <div class="modal__actions">
            <button type="button" class="tbl-btn" data-vmos-md style="margin-right:auto">${t('farms.download_md')}</button>
        </div>
    `;
}
/** Wire copy + download handlers for markup produced by {@link vmosSetupHtml}. */
export function wireVmosSetup(root, s, name) {
    root.querySelector('[data-vmos-md]')?.addEventListener('click', () => {
        downloadMd(s.mdContent, `farm-setup-vmos-${name}`);
    });
    root.querySelector('[data-vmos-copy]')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        void navigator.clipboard.writeText(s.deployCmd);
        btn.textContent = `✓ ${t('common.copied')}`;
        setTimeout(() => { btn.textContent = `⎘ ${t('common.copy')}`; }, 1500);
    });
    root.querySelector('[data-vmos-download]')?.addEventListener('click', () => {
        const blob = new Blob([VMOS_DEPLOY_SCRIPT], { type: 'text/x-shellscript' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'deploy.vmos.sh';
        a.click();
        URL.revokeObjectURL(url);
    });
}
